<template>
  <div id="student" class="application-section">
    <section id="degreeTypeSection">
      <div class="container is-fluid">
      <RadiobuttonQuestion question="I am currently studying for an" :options="degreeTypes" :radioName="degreeTypes.radioName"
        @question-answered="degreeTypeSelected" :class="{error: $v.understandingYou.degreeType.$error }" :isMandatory="true"
        :isDropdownControlled="true" :existingValue="understandingYou.degreeType" :tab="sections.degreeTypeSection.tab"/>
      <FreetextQuestion question="Please provide the full title of your qualification:"
        closingTag="." v-if="isQualificationExtraQuestion" :placeholder="placeholder"
        @question-answered="qualificationEntered" :existingValue="understandingYou.degreeTypeOther"
        :class="{error: $v.understandingYou.degreeTypeOther.$error }" :isMandatory="isQualificationExtraQuestion"
        :isExtraQuestion="true" :isNotDisplayed="true" :tab="sections.degreeTypeSection.tab" maxlength="40"/>
      </div>
    </section>
    <section id="degreePeriod" :class="sections.degreePeriod">
      <div class="container is-fluid">
        <ChooseQuestion question="I started studying for this degree in" :options="degreeStarts"
                        :isDisabled="sections.degreePeriod.ghost" @question-answered="degreeStartSelected"
                        :class="{error: $v.understandingYou.degreeStartYear.$error }" :isMandatory="true"
                        :existingValue="understandingYou.degreeStartYear" :tab="sections.degreePeriod.tab"/>
        <ChooseQuestion question="and I am due to finish in" :options="degreeEnds"
                        :isDisabled="sections.degreePeriod.ghost" :tab="sections.degreePeriod.tab"
                        @question-answered="degreeEndSelected"
                        :class="{error: $v.understandingYou.degreeEndYear.$error }"
                        closingSentence="." :isMandatory="true"
                        :existingValue="understandingYou.degreeEndYear"/>
        <b-field :message="{ 'The finish year needs to be later than the start year.': $v.understandingYou.degreeEndYear.$error}"></b-field>
      </div>
    </section>
    <section id="universitySection" :class="sections.universitySection">
      <div class="container is-fluid">
        <ChooseQuestion question="I am studying at" :options="universities" :tab="sections.universitySection.tab"
                        :isDisabled="sections.universitySection.ghost" @question-answered="universitySelected"
                        :class="{error: $v.understandingYou.university.$error }" closingSentence="." :isMandatory="true"
                        :isDropdownControlled="true" :existingValue="understandingYou.university" />
          <FreetextQuestion question="Please state the name of the institution you are studying:"
                            closingTag="." :placeholder="placeholder" v-if="isUniExtraQuestion"
                            :class="{error: $v.understandingYou.universityOther.$error }" :tab="sections.universitySection.tab"
                            @question-answered="otherInstitutionEntered" :isMandatory="isUniExtraQuestion" :isDropdownControlled="true"
                            :isExtraQuestion="true" :isNotDisplayed="true" :existingValue="understandingYou.universityOther"  maxlength="255"/>
          <b-field :message="{'Name of your institution cannot be left blank.': $v.understandingYou.universityOther.$error}"
                   v-if="isUniExtraQuestion"></b-field>
          <AutocompleteQuestion question="Please state the country in which you are studying:"
                                closingTag="." :placeholder="placeholder" v-if="isUniExtraQuestion"
                                :class="{error: $v.understandingYou.country.$error }" :tab="sections.universitySection.tab"
                                @question-answered="studiedCountryEntered" :isMandatory="isUniExtraQuestion" :isExtraQuestion="true"
                                :isNotDisplayed="true" :existingValue="understandingYou.country"
                                :dataSource="countries"/>
          <b-field :message="{'Please choose the country where you studied.': $v.understandingYou.country.$error}"
                   v-if="isUniExtraQuestion"></b-field>
          <div v-if="!this.$store.state.isMobile" id="confirm-wrap">
            <button class="btn-confirm" @click.stop.prevent="$emit('confirmClicked')" type="button">Next</button>
          </div>
</div>
    </section>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import RadiobuttonQuestion from '@/components/RadiobuttonQuestion.vue';
import FreetextQuestion from '@/components/FreetextQuestion.vue';
import AutocompleteQuestion from '@/components/AutocompleteQuestion.vue';
import ChooseQuestion from '@/components/ChooseQuestion.vue';
import '@/styles/application.scss';
import { required, requiredIf, minValue } from 'vuelidate/lib/validators';
import ghost from '@/understandingyou/scripts/ghost';
import Manager from '@/applicationManager';

export default {
  name: 'Student',
  components: {
    RadiobuttonQuestion,
    FreetextQuestion,
    AutocompleteQuestion,
    ChooseQuestion,
  },
  props: {
    dataGetter: Function,
  },
  async mounted() {
    this.countries = await Manager.getCountryList();
  },
  updated() {
    this.$emit('onFinalSection', this.isFinalSectionVisible);
  },
  data() {
    return {
      degreeTypes: {
        radioName: 'degreeTypes',
        options: [{ text: 'MPharm', value: '1' }, { text: 'Other', value: '2' }],
      },
      isQualificationExtraQuestion: false,
      isUniExtraQuestion: false,
      sections: {
        degreeTypeSection: { hidden: false, ghost: false, tab: 0 },
        degreePeriod: { hidden: false, ghost: true, tab: this.$store.state.isMobile ? -1 : 0 },
        universitySection: { hidden: true, ghost: false, tab: this.$store.state.isMobile ? -1 : 0 },
      },
      placeholder: 'Tell us more',
      understandingYou: this.dataGetter(),
      countries: [],
    };
  },
  computed: {
    ...mapGetters(['degreeStarts', 'degreeEnds', 'universities']),
    validateDegreeTypeOther() {
      return this.understandingYou.degreeType === 'Other' || this.isQualificationExtraQuestion;
    },
    validateUniversityOther() {
      return this.understandingYou.university === 'Other';
    },
    isFinalSectionVisible() {
      return this.sections.universitySection.hidden === false;
    },
  },
  validations() {
    return {
      understandingYou: {
        degreeType: {
          required: requiredIf(() => !this.validateDegreeTypeOther),
        },
        degreeTypeOther: {
          required: requiredIf(() => this.validateDegreeTypeOther),
        },
        degreeStartYear: {
          required,
        },
        degreeEndYear: {
          required,
          minValue: minValue(parseInt(this.understandingYou.degreeStartYear, 10) + 1),
        },
        university: {
          required,
        },
        universityOther: {
          required: requiredIf(() => this.validateUniversityOther),
        },
        country: {
          required,
        },
      },
    };
  },
  methods: {
    HasErrorInSection(currentSection) {
      if (currentSection === 'degreeTypeSection') {
        this.$v.understandingYou.degreeType.$touch();
        this.$v.understandingYou.degreeTypeOther.$touch();
        return this.$v.understandingYou.degreeType.$error || this.$v.understandingYou.degreeTypeOther.$error;
      }
      if (currentSection === 'degreePeriod') {
        this.$v.understandingYou.degreeStartYear.$touch();
        this.$v.understandingYou.degreeEndYear.$touch();
        return this.$v.understandingYou.degreeStartYear.$error || this.$v.understandingYou.degreeEndYear.$error;
      }
      if (currentSection === 'universitySection') {
        this.$v.understandingYou.university.$touch();
        this.$v.understandingYou.universityOther.$touch();
        this.$v.understandingYou.country.$touch();
        return this.$v.understandingYou.university.$error
              || this.$v.understandingYou.universityOther.$error
              || this.$v.understandingYou.country.$error;
      }
      return false;
    },
    degreeTypeSelected(o) {
      this.isQualificationExtraQuestion = o.value === '2';
      this.understandingYou.degreeType = this.isQualificationExtraQuestion ? '' : o.text;
      this.$v.understandingYou.degreeType.$touch();

      ghost.showSection(this.sections.degreePeriod);
      ghost.ghostSection(this.sections.universitySection);
    },
    qualificationEntered(q) {
      this.$v.understandingYou.degreeTypeOther.$touch();
      this.understandingYou.degreeTypeOther = q;
    },
    degreeStartSelected(degreeStart) {
      this.understandingYou.degreeStartYear = degreeStart.text;
      this.$v.understandingYou.degreeStartYear.$touch();
    },
    degreeEndSelected(degreeEnd) {
      this.understandingYou.degreeEndYear = degreeEnd.text;
      this.$v.understandingYou.degreeEndYear.$touch();
      ghost.showSection(this.sections.universitySection);
    },
    universitySelected(u) {
      this.isUniExtraQuestion = u.text === 'Other';
      this.understandingYou.university = u.text;

      if (!this.isUniExtraQuestion) {
        this.understandingYou.country = 'United Kingdom';
      }

      this.$v.understandingYou.university.$touch();
    },
    otherInstitutionEntered(otherUni) {
      this.understandingYou.universityOther = otherUni;
      this.$v.understandingYou.universityOther.$touch();
    },
    studiedCountryEntered(c) {
      this.understandingYou.country = c;
      this.$v.understandingYou.country.$touch();
    },
    SetIndexes(current, next) {
      this.sections[current].tab = -1;
      this.sections[next].tab = 0;
    },
  },
};
</script>
<style>
  #degreePeriod div.field p.help {
    font-family: "Poppins", Arial, sans-serif;
    font-weight: 100;
    font-size: 12px;
    color: red;
  }
  #universitySection div.field p.help {
    font-family: "Poppins", Arial, sans-serif;
    font-weight: 100;
    font-size: 12px;
    color: red;
    margin-bottom: 30px
  }
  #universitySection div.error div.dropdowncontrol {
      margin-bottom: 5px;
  }
</style>
